import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ShieldCheck,
  Building2,
  Calendar,
  Layers,
  Sparkles,
  UploadCloud,
  FileSpreadsheet,
  GitCompare,
  FileCheck2,
  Plus,
  LogIn,
  LogOut,
  ChevronDown,
  ExternalLink,
  Sun,
  Moon
} from 'lucide-react';
import { useGst, FINANCIAL_YEARS, TAX_PERIODS } from '../context/GstContext';

export const GlobalHeader: React.FC = () => {
  const location = useLocation();
  const {
    user,
    userRole,
    clientLegalName,
    isSuperOrCa,
    gstProfiles,
    activeGstin,
    financialYear,
    taxPeriod,
    invoices,
    gstr2bRecords,
    copilotOpen,
    themeMode,
    toggleTheme,
    setCopilotOpen,
    setSecurityModalOpen,
    setActiveGstin,
    setFinancialYear,
    setTaxPeriod,
    setShowProfileModal,
    signInWithGoogle,
    logout
  } = useGst();

  const navLinks = [
    { name: 'Upload Vault', path: '/vault', icon: UploadCloud, count: invoices.length },
    { name: 'Review Ledger', path: '/ledger', icon: FileSpreadsheet, badge: invoices.filter(i => !i.math_verified).length > 0 ? 'Review' : undefined },
    { name: '3-Way Recon', path: '/reconciliation', icon: GitCompare, badge: invoices.filter(i => i.recon_status === 'MISSING_IN_2B').length > 0 ? 'At Risk' : undefined },
    { name: 'Returns & Annual', path: '/returns', icon: FileCheck2 },
  ];

  return (
    <header className="sticky top-0 z-40 w-full border-b border-[rgba(247,247,248,0.08)] bg-[#0e1015]/95 backdrop-blur-md">
      {/* Top Banner / Client Context */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[rgba(247,247,248,0.05)] text-xs text-[#94a3b8]">
        <div className="flex items-center gap-3">
          <a
            href="https://verveai.co"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 group transition"
            title="Visit Verve AI Labs Sovereign Cloud & AI Systems (verveai.co)"
          >
            <div className="relative h-7 w-7 rounded-xl bg-gradient-to-tr from-[#07080a] via-[#111317] to-[#0e1015] border border-cyan-500/40 p-1 flex items-center justify-center shadow-sm shadow-cyan-500/25 group-hover:border-cyan-400 transition">
              <img
                src="/logo.svg"
                alt="Verve AI Labs"
                className="h-full w-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src.endsWith(".svg")) {
                    target.src = "/logo.png";
                  }
                }}
              />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-white tracking-wide text-xs">
                  Verve <span className="text-cyan-400">AI Labs</span>
                </span>
                <span className="text-[9px] font-mono uppercase px-1 py-0.2 rounded border border-cyan-500/30 bg-cyan-950/60 text-cyan-300">
                  GST
                </span>
              </div>
              <span className="text-[9px] text-slate-500 font-medium -mt-0.5 group-hover:text-cyan-300 transition flex items-center gap-0.5">
                <span>Subsidiary of verveai.co</span>
                <ExternalLink className="h-2 w-2" />
              </span>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-2 pl-4 border-l border-white/10">
            <Building2 className="h-3.5 w-3.5 text-cyan-400" />
            <span className="text-slate-300 font-medium truncate max-w-[200px]">
              {clientLegalName}
            </span>
            {isSuperOrCa && (
              <span className="rounded bg-cyan-950/80 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-300 border border-cyan-800/40">
                CA / ADMIN VIEW
              </span>
            )}
          </div>
        </div>

        {/* Auth / Security / Status / Theme Switch Pill */}
        <div className="flex items-center gap-2.5">
          {/* Theme Mode Toggle (Dark / Light Switch) */}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition border border-white/10 hover:border-cyan-500/40 bg-white/[0.04] text-slate-300 hover:text-white"
            title={themeMode === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
          >
            {themeMode === 'light' ? (
              <>
                <Moon className="h-3.5 w-3.5 text-indigo-500" />
                <span className="hidden sm:inline text-[11px] font-semibold text-slate-800">Dark</span>
              </>
            ) : (
              <>
                <Sun className="h-3.5 w-3.5 text-amber-400" />
                <span className="hidden sm:inline text-[11px] font-semibold text-slate-300">Light</span>
              </>
            )}
          </button>

          {/* Enterprise Data Shield Trigger Button */}
          <button
            onClick={() => setSecurityModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-emerald-300 bg-emerald-950/40 border border-emerald-500/30 hover:border-emerald-400 hover:bg-emerald-950/60 transition shadow-sm"
            title="Open Enterprise Data Shield & Security Governance Center"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            <span className="hidden sm:inline">Data Shield:</span>
            <span className="font-mono text-[10px]">AES-256</span>
          </button>

          <a
            href="https://verveai.co"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono text-slate-400 hover:text-cyan-300 border border-white/5 hover:border-cyan-500/30 transition bg-white/[0.02]"
            title="Verve AI Labs Main Portal"
          >
            <span>verveai.co</span>
            <ExternalLink className="h-3 w-3" />
          </a>

          <button
            onClick={() => setCopilotOpen(!copilotOpen)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
              copilotOpen
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                : 'bg-white/[0.05] text-slate-300 hover:bg-white/[0.1] border border-white/10'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5 text-cyan-400 animate-pulse" />
            <span>AI Copilot</span>
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-slate-400 truncate max-w-[140px]">
                {user.email}
              </span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold border ${
                userRole === 'ADMIN'
                  ? 'bg-amber-950/70 text-amber-300 border-amber-800/40'
                  : 'bg-cyan-950/70 text-cyan-300 border-cyan-800/40'
              }`}>
                {userRole}
              </span>
              <button
                onClick={logout}
                title="Sign Out"
                className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/5 transition"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={signInWithGoogle}
              className="flex items-center gap-1.5 px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-black font-semibold text-xs transition"
            >
              <LogIn className="h-3 w-3" />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>

      {/* Global Period & Entity Selector Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-[#0b0c0e]">
        {/* Navigation Tabs */}
        <nav className="flex items-center gap-1 overflow-x-auto">
          {navLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path || (link.path === '/vault' && location.pathname === '/');
            return (
              <Link
                key={link.name}
                to={link.path}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition shrink-0 ${
                  isActive
                    ? 'bg-white/10 text-white font-semibold ring-1 ring-cyan-500/40'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-cyan-400' : 'text-slate-400'}`} />
                <span>{link.name}</span>
                {link.badge && (
                  <span className="rounded bg-rose-500/20 text-rose-300 px-1.5 py-0.2 text-[9px] font-bold border border-rose-500/30">
                    {link.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Period Selectors */}
        <div className="flex items-center gap-2 shrink-0">
          {/* GSTIN Selector */}
          <div className="flex items-center gap-1 bg-[#131316] border border-white/10 rounded-lg px-2.5 py-1 text-xs">
            <span className="text-[10px] uppercase font-bold text-slate-500">GSTIN:</span>
            {gstProfiles.length > 0 ? (
              <select
                value={activeGstin}
                onChange={(e) => setActiveGstin(e.target.value)}
                className="bg-transparent text-slate-200 font-mono text-xs focus:outline-none cursor-pointer"
              >
                {gstProfiles.map((p) => (
                  <option key={p.gstin} value={p.gstin} className="bg-[#131316] text-white">
                    {p.gstin} ({p.stateName})
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-slate-400">None</span>
            )}
            <button
              onClick={() => setShowProfileModal(true)}
              title="Add or Edit GSTIN Entity Profile"
              className="ml-1 text-cyan-400 hover:text-cyan-300 p-0.5"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>

          {/* Financial Year Selector */}
          <div className="flex items-center gap-1 bg-[#131316] border border-white/10 rounded-lg px-2.5 py-1 text-xs">
            <Calendar className="h-3 w-3 text-cyan-400" />
            <span className="text-[10px] uppercase font-bold text-slate-500">FY:</span>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="bg-transparent text-slate-200 font-medium text-xs focus:outline-none cursor-pointer"
            >
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy} value={fy} className="bg-[#131316] text-white">
                  {fy}
                </option>
              ))}
            </select>
          </div>

          {/* Tax Period Selector */}
          <div className="flex items-center gap-1 bg-[#131316] border border-white/10 rounded-lg px-2.5 py-1 text-xs">
            <span className="text-[10px] uppercase font-bold text-slate-500">PERIOD:</span>
            <select
              value={taxPeriod}
              onChange={(e) => setTaxPeriod(e.target.value)}
              className="bg-transparent text-cyan-300 font-semibold text-xs focus:outline-none cursor-pointer"
            >
              {TAX_PERIODS.map((p) => (
                <option key={p} value={p} className="bg-[#131316] text-white">
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </header>
  );
};
